import { useMemo, useState, useEffect, useCallback, useRef } from "react";
import Sidebar from "./Sidebar";
import HinglishTextarea from "./HinglishTextarea";
import {
  assignAudio,
  assignVadAudio,
  saveAnnotation,
  skipTask,
  getTasks,
  API_BASE_URL,
} from "../services/api";
import { useEventLogger } from "../hooks/useEventLogger";
import { transcribeAudioOmli } from "../hooks/useLiveSTT";
import { getSarvamTranscript, assignValidationAudio } from "../services/api";

// Active validation run. Remove this constant + the request button when the
// run is fully annotated and you don't want annotators to keep claiming.
const ACTIVE_VALIDATION_RUN_ID = "stt_val_2026_05_audit_1000";

// Anti-monotony calibration check: every so often the annotator is forced
// to write the transcript from scratch (model suggestions are hidden).
// The interval is randomized between MIN..MAX so it stays unpredictable.
const CALIBRATION_GAP_MIN = 7;
const CALIBRATION_GAP_MAX = 13;
const pickNextCalibrationAt = (current) =>
  current +
  CALIBRATION_GAP_MIN +
  Math.floor(Math.random() * (CALIBRATION_GAP_MAX - CALIBRATION_GAP_MIN + 1));

const buildAudioUrl = (taskId) => {
  const token = localStorage.getItem("companioToken");
  if (!taskId || !token) return "";
  return `${API_BASE_URL}/tasks/${taskId}/audio-stream?token=${encodeURIComponent(token)}`;
};

async function copyToClipboard(text) {
  if (!text) return false;
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
    // Fallback for non-HTTPS / older browsers.
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

const formatTask = (task) => {
  const sarvam = task.transcript || "";
  // Per-task random assignment of which model is "Transcript 1" vs
  // "Transcript 2". Removes positional bias when the annotator picks.
  // The internal source identifier is still recorded in audit fields so
  // downstream analysis is unaffected.
  const sarvamInSlot1 = Math.random() < 0.5;
  const slot1Source = sarvamInSlot1 ? "sarvam" : "omli";
  const slot2Source = sarvamInSlot1 ? "omli" : "sarvam";
  return {
    id: task.id,
    title: task.title,
    transcript: sarvam,
    originalTranscript: sarvam,
    customTranscript: "",
    transcriptSource: null,
    slot1Source,
    slot2Source,
    environment: "normal",
    status: task.status,
    audioType: "child",
    interactionType: "to_doro",
    comments: "",
    transcriptWrong: false,
    redFlag: false,
    source: task.source ?? null,
    innerStartSec: task.inner_start_sec ?? null,
    innerEndSec: task.inner_end_sec ?? null,
    chunkDurationSec: task.duration_sec ?? null,
    validationRunId: task.validation_run_id ?? null,
  };
};

export default function DashboardV2({ user, onPageChange }) {
  const [userName] = useState(user?.name || "User");
  const [tasks, setTasks] = useState([]);
  const [activeTaskId, setActiveTaskId] = useState(null);
  const [audioUrl, setAudioUrl] = useState("");
  const [isRequesting, setIsRequesting] = useState(false);
  const [isRequestingVad, setIsRequestingVad] = useState(false);
  const [requestMessage, setRequestMessage] = useState("");
  const [selectedAgeGroup, setSelectedAgeGroup] = useState("3_4");
  const [vadFilterType, setVadFilterType] = useState("single_speaker_child");
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ show: false, message: "", type: "" });
  const [completedCount, setCompletedCount] = useState(() => {
    if (typeof window === "undefined") return 0;
    const key = `companio_annot_count_${user?.email || ""}`;
    const stored = localStorage.getItem(key);
    return stored ? parseInt(stored, 10) || 0 : 0;
  });
  const [nextCalibrationAt, setNextCalibrationAt] = useState(() => {
    if (typeof window === "undefined") return CALIBRATION_GAP_MIN;
    const key = `companio_next_calibration_${user?.email || ""}`;
    const stored = localStorage.getItem(key);
    if (stored) {
      const n = parseInt(stored, 10);
      if (Number.isFinite(n) && n > 0) return n;
    }
    const initial = pickNextCalibrationAt(0);
    try { localStorage.setItem(key, String(initial)); } catch {}
    return initial;
  });
  // Map keyed by task id: { [taskId]: { sarvam: {text, status, error}, omli: {...} } }
  // Populated once per task batch — switching tasks afterwards is a cache hit.
  const [transcriptCache, setTranscriptCache] = useState({});
  // AbortControllers per (taskId, source) so prefetch cancels cleanly on
  // unmount / new audio batch.
  const prefetchControllersRef = useRef({});

  const audioRef = useRef(null);
  const audioDurationRef = useRef(null);

  const {
    logEvent,
    logAudioPlay,
    logTranscriptChange,
    logOptionSelect,
    logSubmit,
    logSkip,
    logAudioSelect,
    logNavigation,
    logLogout,
  } = useEventLogger(user);
  const audioStartTimeRef = useRef(null);
  const transcriptChangedRef = useRef(false);
  const debounceTimeoutRef = useRef(null);
  const commentsChangedRef = useRef(false);
  const commentsDebounceTimeoutRef = useRef(null);

  useEffect(() => {
    const fetchTasks = async () => {
      try {
        const data = await getTasks();
        const formatted = data.map(formatTask);
        setTasks(formatted);
        if (formatted.length > 0) setActiveTaskId(formatted[0].id);
      } catch (error) {
        console.error("Failed to fetch tasks:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchTasks();
  }, []);

  const activeTask = useMemo(
    () => tasks.find((t) => t.id === activeTaskId) || tasks[0] || {},
    [tasks, activeTaskId]
  );

  useEffect(() => {
    if (activeTaskId) {
      setAudioUrl(buildAudioUrl(activeTaskId));
      transcriptChangedRef.current = false;
      commentsChangedRef.current = false;
    } else {
      setAudioUrl("");
    }
  }, [activeTaskId]);

  // Cache helpers + prefetch.
  const updateCacheSlot = useCallback((taskId, source, partial) => {
    setTranscriptCache((prev) => ({
      ...prev,
      [taskId]: {
        ...prev[taskId],
        [source]: { ...(prev[taskId]?.[source] || {}), ...partial },
      },
    }));
  }, []);

  const prefetchSarvamFor = useCallback(async (task) => {
    if (!task || !task.id) return;
    // If the batch pipeline already filled it, populate cache instantly.
    if (task.transcript && task.transcript.trim().length > 0) {
      updateCacheSlot(task.id, "sarvam", { text: task.transcript, status: "done" });
      return;
    }
    updateCacheSlot(task.id, "sarvam", { text: "", status: "loading", error: null });
    try {
      const data = await getSarvamTranscript(task.id);
      if (data && data.error) {
        updateCacheSlot(task.id, "sarvam", { text: "", status: "error", error: data.error });
      } else {
        const t = (data && data.transcript ? String(data.transcript) : "").trim();
        updateCacheSlot(task.id, "sarvam", { text: t, status: "done" });
      }
    } catch (e) {
      updateCacheSlot(task.id, "sarvam", {
        text: "",
        status: "error",
        error: e?.response?.data?.detail || e?.message || "Failed",
      });
    }
  }, [updateCacheSlot]);

  const prefetchOmliFor = useCallback(async (task) => {
    if (!task || !task.id) return;
    const audioUrl = buildAudioUrl(task.id);
    if (!audioUrl) return;
    const controller = new AbortController();
    prefetchControllersRef.current[`${task.id}:omli`] = controller;
    updateCacheSlot(task.id, "omli", { text: "", status: "loading", error: null });
    try {
      const text = await transcribeAudioOmli(audioUrl, {
        signal: controller.signal,
        onPartial: (partial) => {
          updateCacheSlot(task.id, "omli", { text: partial, status: "streaming" });
        },
      });
      updateCacheSlot(task.id, "omli", { text, status: "done" });
    } catch (e) {
      if (e?.name === "AbortError") return;
      updateCacheSlot(task.id, "omli", {
        text: "",
        status: "error",
        error: e?.message || "Failed",
      });
    } finally {
      delete prefetchControllersRef.current[`${task.id}:omli`];
    }
  }, [updateCacheSlot]);

  // Whenever the task list changes, prefetch transcripts for any new tasks.
  // Sarvam fans out in parallel (cheap HTTP). OMLI runs sequentially with
  // the active task first so the user isn't blocked waiting on others.
  useEffect(() => {
    const todo = tasks.filter((t) => !transcriptCache[t.id]);
    if (todo.length === 0) return undefined;

    todo.forEach((t) => { prefetchSarvamFor(t); });

    const ordered = activeTaskId
      ? [
          ...todo.filter((t) => t.id === activeTaskId),
          ...todo.filter((t) => t.id !== activeTaskId),
        ]
      : todo;
    let cancelled = false;
    (async () => {
      for (const t of ordered) {
        if (cancelled) break;
        await prefetchOmliFor(t);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks]);

  // Abort all in-flight prefetches on unmount.
  useEffect(() => {
    return () => {
      Object.values(prefetchControllersRef.current).forEach((c) => {
        try { c.abort(); } catch {}
      });
      prefetchControllersRef.current = {};
    };
  }, []);

  // Read cache for the active task.
  const sarvamSlot = transcriptCache[activeTaskId]?.sarvam || { text: "", status: "idle" };
  const omliSlot = transcriptCache[activeTaskId]?.omli || { text: "", status: "idle" };
  const sarvamTranscript = sarvamSlot.text || "";
  const sarvamStatus = sarvamSlot.status;
  const sarvamError = sarvamSlot.error;
  const sttTranscript = omliSlot.text || "";
  const sttStatus = omliSlot.status;
  const sttError = omliSlot.error;

  // Audit-run tasks always require a custom transcript — the dataset is
  // being used to score annotator transcripts against, so model
  // suggestions must not anchor what the annotator types.
  const isAuditRun =
    !!activeTask?.validationRunId &&
    activeTask.validationRunId === ACTIVE_VALIDATION_RUN_ID;

  // Calibration check: a single task ahead of us, randomly scheduled. The
  // 1-indexed task number being shown (completedCount + 1) matches the
  // pre-rolled `nextCalibrationAt`. After a calibration submit we roll a
  // fresh gap so the next one stays unpredictable.
  const isForcedCustom =
    isAuditRun ||
    (activeTaskId && completedCount + 1 === nextCalibrationAt);

  // When a forced-custom task becomes active, hard-lock its source to
  // 'custom'. We don't render the model slots in this mode anyway.
  useEffect(() => {
    if (isForcedCustom && activeTaskId) {
      setTasks((prev) =>
        prev.map((t) =>
          t.id === activeTaskId && t.transcriptSource !== "custom"
            ? { ...t, transcriptSource: "custom" }
            : t
        )
      );
    }
  }, [isForcedCustom, activeTaskId]);

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: "", type: "" }), 3000);
  };

  const handleFieldChange = (field, value) => {
    setTasks((prev) =>
      prev.map((task) => (task.id === activeTaskId ? { ...task, [field]: value } : task))
    );
    if (activeTaskId && field !== "customTranscript" && field !== "comments") {
      logOptionSelect(activeTaskId, field, value);
    }
  };

  const handleSkip = useCallback(async () => {
    try {
      logSkip(activeTask.id);
      await skipTask(activeTask.id);
      showToast("✓ Task skipped successfully!");
      setTasks((prev) => prev.filter((task) => task.id !== activeTaskId));
      const nextTask = tasks.find((task) => task.id !== activeTaskId);
      if (nextTask) setActiveTaskId(nextTask.id);
    } catch (error) {
      showToast(error.response?.data?.detail || "Failed to skip task", "error");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTask, activeTaskId, tasks]);

  const resolveFinalTranscript = (task) => {
    if (!task) return "";
    if (task.transcriptSource === "sarvam") return sarvamTranscript || "";
    if (task.transcriptSource === "omli") return sttTranscript || "";
    return task.customTranscript || "";
  };

  const markComplete = useCallback(async () => {
    try {
      const timeFromAudioStart = audioStartTimeRef.current
        ? Date.now() - audioStartTimeRef.current
        : null;
      logSubmit(activeTaskId, timeFromAudioStart);

      const finalTranscript = resolveFinalTranscript(activeTask);
      const annotationData = {
        audio_type: activeTask.audioType,
        interaction_type: activeTask.interactionType,
        transcript: sarvamTranscript || "",
        updated_transcript: finalTranscript,
        transcript_changed: finalTranscript !== (sarvamTranscript || ""),
        transcript_wrong: activeTask.transcriptWrong || false,
        environment: activeTask.environment,
        comments: activeTask.comments,
        red_flag: activeTask.redFlag,
        duration: audioDurationRef.current,
        regions: [],
        // Audit fields so we can later analyze which source annotators trust,
        // and reconstruct positional bias from the randomized slot order.
        transcript_source: activeTask.transcriptSource,
        sarvam_transcript: sarvamTranscript || "",
        omli_stt_transcript: sttTranscript || "",
      };

      await saveAnnotation(activeTask.id, annotationData);
      showToast("✓ Annotation saved successfully!");

      // Bump the calibration counter; persists across reloads / re-logins.
      const newCount = completedCount + 1;
      const wasCalibration = newCount === nextCalibrationAt;
      setCompletedCount(newCount);
      try {
        localStorage.setItem(
          `companio_annot_count_${user?.email || ""}`,
          String(newCount)
        );
      } catch {}
      // If this submission cleared a calibration task, roll the next gap.
      if (wasCalibration) {
        const newNext = pickNextCalibrationAt(newCount);
        setNextCalibrationAt(newNext);
        try {
          localStorage.setItem(
            `companio_next_calibration_${user?.email || ""}`,
            String(newNext)
          );
        } catch {}
      }

      setTasks((prev) => {
        const updated = prev.filter((task) => task.id !== activeTaskId);
        const nextTask = updated[0];
        if (nextTask) setActiveTaskId(nextTask.id);
        return updated;
      });
    } catch (error) {
      showToast(error.response?.data?.detail || "Failed to save annotation", "error");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTask, activeTaskId, sttTranscript, sarvamTranscript, user, completedCount, nextCalibrationAt]);

  const handleRequestAudio = useCallback(async () => {
    setIsRequesting(true);
    setRequestMessage("");
    try {
      await assignAudio(selectedAgeGroup);
      const data = await getTasks();
      const formatted = data.map(formatTask);
      setTasks(formatted);
      if (formatted.length > 0) setActiveTaskId(formatted[0].id);
    } catch (error) {
      setRequestMessage(`✗ ${error.response?.data?.detail || "Failed to request audio"}`);
      setTimeout(() => setRequestMessage(""), 3000);
    } finally {
      setIsRequesting(false);
    }
  }, [selectedAgeGroup]);

  const handleRequestVadAudio = useCallback(async () => {
    setIsRequestingVad(true);
    setRequestMessage("");
    try {
      await assignVadAudio(vadFilterType);
      const data = await getTasks();
      const formatted = data.map(formatTask);
      setTasks(formatted);
      if (formatted.length > 0) setActiveTaskId(formatted[0].id);
    } catch (error) {
      setRequestMessage(`✗ ${error.response?.data?.detail || "Failed to request VAD audio"}`);
      setTimeout(() => setRequestMessage(""), 3000);
    } finally {
      setIsRequestingVad(false);
    }
  }, [vadFilterType]);

  const [isRequestingValidation, setIsRequestingValidation] = useState(false);
  const handleRequestValidationAudio = useCallback(async () => {
    setIsRequestingValidation(true);
    setRequestMessage("");
    try {
      await assignValidationAudio(ACTIVE_VALIDATION_RUN_ID);
      const data = await getTasks();
      const formatted = data.map(formatTask);
      setTasks(formatted);
      if (formatted.length > 0) setActiveTaskId(formatted[0].id);
    } catch (error) {
      setRequestMessage(`✗ ${error.response?.data?.detail || "No validation audio available"}`);
      setTimeout(() => setRequestMessage(""), 3000);
    } finally {
      setIsRequestingValidation(false);
    }
  }, []);

  if (loading) return <div className="app">Loading tasks...</div>;

  const renderRequestPanel = () => (
    <div>
      <div
        className="age-group-selector"
        style={{ marginBottom: "8px", display: "flex", flexWrap: "wrap", gap: "6px" }}
      >
        {[
          { value: "single_speaker_child", label: "1 spk · child" },
          { value: "single_speaker_parent", label: "1 spk · parent" },
          { value: "multiple_speakers", label: "Multi spk" },
          { value: "unclassified", label: "None" },
        ].map((opt) => {
          const active = vadFilterType === opt.value;
          return (
            <button
              key={opt.value}
              onClick={() => setVadFilterType(opt.value)}
              style={{
                padding: "6px 12px",
                fontSize: "12px",
                fontWeight: 500,
                borderRadius: "6px",
                cursor: "pointer",
                border: "1px solid #7c3aed",
                backgroundColor: active ? "#7c3aed" : "#ffffff",
                color: active ? "#ffffff" : "#7c3aed",
              }}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
      <button
        className="request-audio-btn"
        onClick={handleRequestVadAudio}
        disabled={isRequestingVad}
        style={{ marginBottom: "12px", backgroundColor: "#7c3aed", borderColor: "#7c3aed" }}
      >
        {isRequestingVad ? "Requesting..." : "Request VAD Audio"}
      </button>
      <button
        className="request-audio-btn"
        onClick={handleRequestValidationAudio}
        disabled={isRequestingValidation}
        style={{ marginBottom: "12px", backgroundColor: "#dc2626", borderColor: "#dc2626", color: "#fff" }}
        title={`Validation run: ${ACTIVE_VALIDATION_RUN_ID}`}
      >
        {isRequestingValidation ? "Requesting..." : "Request Validation Audio"}
      </button>
      <div className="age-group-selector">
        {["", "3_4", "5_6", "7_8", "9_gte"].map((ag) => (
          <button
            key={ag || "all"}
            className={`age-group-btn${selectedAgeGroup === ag ? " active" : ""}`}
            onClick={() => setSelectedAgeGroup(ag)}
          >
            {ag || "All"}
          </button>
        ))}
      </div>
      <button className="request-audio-btn" onClick={handleRequestAudio} disabled={isRequesting}>
        {isRequesting ? "Requesting..." : "Request Audio"}
      </button>
      {requestMessage && <div className="request-message error">{requestMessage}</div>}
    </div>
  );

  if (!activeTask || !activeTask.id) {
    return (
      <div className="workspace">
        <Sidebar
          user={user}
          currentPage="dashboard"
          onPageChange={onPageChange}
          title={userName}
          subtitle=""
          onLogout={logLogout}
          onNavigation={logNavigation}
        >
          <div className="queue-header">
            <h3>Stacked audios</h3>
            <span>0 assigned</span>
          </div>
          {renderRequestPanel()}
        </Sidebar>
        <main className="annotator">
          <div className="empty-state" style={{ padding: "48px", textAlign: "center" }}>
            <p>No tasks available. Request audio to get started.</p>
          </div>
        </main>
      </div>
    );
  }

  // On calibration tasks where the audio actually has speech, require a
  // non-empty custom transcript. For noise/silence the textarea naturally
  // stays empty, so we don't block submit.
  const audioHasSpeech =
    activeTask.audioType === "child" || activeTask.audioType === "adult";
  const calibrationBlocked =
    isForcedCustom &&
    audioHasSpeech &&
    (!activeTask.customTranscript || activeTask.customTranscript.trim().length === 0);

  const submitDisabled =
    !activeTask.audioType ||
    (audioHasSpeech && !activeTask.interactionType) ||
    calibrationBlocked;

  return (
    <div className="workspace">
      {toast.show && <div className={`toast toast-${toast.type}`}>{toast.message}</div>}
      <Sidebar
        user={user}
        currentPage="dashboard"
        onPageChange={onPageChange}
        title={userName}
        subtitle=""
        onLogout={logLogout}
        onNavigation={logNavigation}
      >
        <div className="queue-header">
          <h3>Stacked audios</h3>
          <span>{tasks.length} assigned</span>
        </div>
        {tasks.length === 0 ? (
          renderRequestPanel()
        ) : (
          <ul className="queue">
            {tasks.map((task) => (
              <li
                key={task.id}
                className={`queue-item ${task.id === activeTaskId ? "active" : ""}`}
                onClick={() => {
                  logAudioSelect(task.id);
                  setActiveTaskId(task.id);
                }}
              >
                <div>
                  <strong>{task.title}</strong>
                </div>
                <span className={`status ${task.status}`}>{task.status}</span>
              </li>
            ))}
          </ul>
        )}
      </Sidebar>

      <main className="annotator">
        <header className="annotator-header">
          <div>
            <span className="badge">Now playing</span>
            <h1>{activeTask.title}</h1>
            <p>Clip ID {activeTask.id}</p>
          </div>
        </header>

        <section className="audio-card" style={{ borderRadius: 12, padding: "16px" }}>
          <audio
            ref={audioRef}
            key={activeTask.id}
            src={audioUrl}
            controls
            preload="auto"
            controlsList="nodownload noplaybackrate"
            onLoadedMetadata={(e) => {
              audioDurationRef.current = e.target.duration;
            }}
            onPlay={() => {
              audioStartTimeRef.current = Date.now();
              logAudioPlay(activeTaskId);
            }}
            style={{ width: "100%", outline: "none" }}
          />
        </section>

        <section
          className="annotation-grid"
          style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}
        >
          {/* Transcript Panel — 3-way picker */}
          <div className="panel" style={{ gridColumn: "1 / -1" }}>
            <h3
              style={{
                color: "#374151",
                fontWeight: "600",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 14c1.66 0 2.99-1.34 2.99-3L15 5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.48 6-3.3 6-6.72h-1.7z" />
              </svg>
              Choose the correct transcript
            </h3>

            {isForcedCustom && (
              <div
                style={{
                  marginBottom: "12px",
                  padding: "10px 14px",
                  background: "#fef3c7",
                  border: "1px solid #f59e0b",
                  borderRadius: "8px",
                  fontSize: "14px",
                  color: "#92400e",
                }}
              >
                {isAuditRun
                  ? "Audit clip — please listen carefully and type the transcript yourself. Model suggestions are hidden for this dataset."
                  : "Calibration check — please listen carefully and type the correct transcript yourself. Model suggestions are hidden for this clip."}
                {calibrationBlocked && (
                  <div style={{ marginTop: 6, fontWeight: 600 }}>
                    ⚠️ Type the transcript before you can submit.
                  </div>
                )}
              </div>
            )}

            <div style={{ display: "grid", gap: "12px" }}>
              {!isForcedCustom && [activeTask.slot1Source, activeTask.slot2Source].map((slotSource, i) => {
                const slotNumber = i + 1;
                const isOmli = slotSource === "omli";
                const text = isOmli ? sttTranscript : sarvamTranscript;
                const status = isOmli ? sttStatus : sarvamStatus;
                const err = isOmli ? sttError : sarvamError;
                let badge = null;
                if (status === "loading") badge = "loading…";
                else if (status === "streaming") badge = "streaming…";
                else if (status === "error") badge = "error";
                else if (status === "done") badge = "ready";
                const badgeColor =
                  status === "error" ? "#dc2626" : status === "done" ? "#10b981" : "#7c3aed";
                const isEmpty = !text && status !== "streaming";
                const emptyMessage = err
                  ? "Transcript unavailable"
                  : status === "loading"
                  ? "Loading transcript…"
                  : status === "streaming"
                  ? "Transcribing…"
                  : "Transcript unavailable";
                return (
                  <TranscriptCard
                    key={`slot-${slotNumber}-${slotSource}`}
                    title={`Transcript ${slotNumber}`}
                    selected={activeTask.transcriptSource === slotSource}
                    onSelect={() => handleFieldChange("transcriptSource", slotSource)}
                    copyText={text}
                    badge={badge}
                    badgeColor={badgeColor}
                    empty={isEmpty}
                    emptyMessage={emptyMessage}
                  >
                    <div
                      style={{
                        padding: "12px",
                        fontSize: "15px",
                        lineHeight: "1.6",
                        color: "#374151",
                        whiteSpace: "pre-wrap",
                      }}
                    >
                      {text || "—"}
                    </div>
                  </TranscriptCard>
                );
              })}

              <TranscriptCard
                title="Write your own"
                selected={activeTask.transcriptSource === "custom"}
                onSelect={() => handleFieldChange("transcriptSource", "custom")}
              >
                <HinglishTextarea
                  value={activeTask.customTranscript}
                  onFocus={() => handleFieldChange("transcriptSource", "custom")}
                  onChange={(event) => {
                    const newValue = event.target.value;
                    handleFieldChange("customTranscript", newValue);
                    if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
                    debounceTimeoutRef.current = setTimeout(() => {
                      if (!transcriptChangedRef.current && newValue.length > 0) {
                        logTranscriptChange(activeTaskId, 0, newValue.length);
                        transcriptChangedRef.current = true;
                      }
                    }, 1000);
                  }}
                  style={{
                    width: "100%",
                    minHeight: "80px",
                    fontSize: "15px",
                    lineHeight: "1.6",
                    padding: "12px",
                    border: "none",
                    fontFamily: "system-ui, -apple-system, sans-serif",
                    resize: "vertical",
                    backgroundColor: "transparent",
                    color: "var(--text-color, #374151)",
                    outline: "none",
                  }}
                  placeholder="Type the correct transcript here…"
                />
              </TranscriptCard>
            </div>

            <div
              style={{
                marginTop: "16px",
                display: "flex",
                gap: "8px",
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#374151",
                  cursor: "pointer",
                  padding: "8px 12px",
                  borderRadius: "6px",
                  backgroundColor: activeTask.transcriptWrong ? "#fef2f2" : "#f9fafb",
                  border: `1px solid ${activeTask.transcriptWrong ? "#fecaca" : "#e5e7eb"}`,
                  transition: "all 0.2s ease",
                }}
              >
                <input
                  type="checkbox"
                  checked={activeTask.transcriptWrong === true}
                  onChange={(e) => handleFieldChange("transcriptWrong", e.target.checked)}
                  style={{ width: "18px", height: "18px", accentColor: "#ef4444", cursor: "pointer" }}
                />
                <span style={{ color: activeTask.transcriptWrong ? "#dc2626" : "#374151" }}>
                  Transcript doesn't match audio
                </span>
              </label>

              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#374151",
                  cursor: "pointer",
                  padding: "8px 10px",
                  borderRadius: "6px",
                  backgroundColor: activeTask.redFlag ? "#fee2e2" : "#fef9f9",
                  border: `1px solid ${activeTask.redFlag ? "#dc2626" : "#f87171"}`,
                  transition: "all 0.2s ease",
                }}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  style={{ color: activeTask.redFlag ? "#dc2626" : "#ef4444" }}
                >
                  <path d="M14.4 6L14 4H5v17h2v-7h5.6l.4 2h7V6z" />
                </svg>
                <input
                  type="checkbox"
                  checked={activeTask.redFlag === true}
                  onChange={(e) => handleFieldChange("redFlag", e.target.checked)}
                  style={{ width: "18px", height: "18px", accentColor: "#dc2626", cursor: "pointer" }}
                />
                <span
                  style={{
                    color: activeTask.redFlag ? "#dc2626" : "#374151",
                    fontWeight: "500",
                  }}
                >
                  Flag Inappropriate
                </span>
              </label>
            </div>
          </div>

          {/* Primary classification */}
          <div className="panel" style={{ gridColumn: "1 / -1" }}>
            <h3 style={{ color: "#374151", fontWeight: "600" }}>Primary Classification</h3>
            <div style={{ display: "grid", gap: "20px" }}>
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontWeight: "600",
                    color: "#374151",
                  }}
                >
                  Audio Type:
                </label>
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                  {["noise", "silence", "child", "adult"].map((type) => (
                    <button
                      key={type}
                      type="button"
                      style={{
                        padding: "8px 16px",
                        fontSize: "14px",
                        fontWeight: "500",
                        backgroundColor:
                          activeTask.audioType === type ? "#3b82f6" : "#f8fafc",
                        color: activeTask.audioType === type ? "white" : "#475569",
                        border: `2px solid ${
                          activeTask.audioType === type ? "#3b82f6" : "#e2e8f0"
                        }`,
                        borderRadius: "8px",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        textTransform: "capitalize",
                      }}
                      onClick={() => {
                        handleFieldChange("audioType", type);
                        handleFieldChange("interactionType", null);
                      }}
                    >
                      {type}
                    </button>
                  ))}
                </div>
              </div>

              {(activeTask.audioType === "child" || activeTask.audioType === "adult") && (
                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontWeight: "600",
                      color: "#374151",
                    }}
                  >
                    Interaction Type:
                  </label>
                  <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                    {(activeTask.audioType === "child"
                      ? ["to_doro", "to_parent", "background"]
                      : ["to_doro", "to_child", "background"]
                    ).map((type) => (
                      <button
                        key={type}
                        type="button"
                        style={{
                          padding: "8px 16px",
                          fontSize: "14px",
                          fontWeight: "500",
                          backgroundColor:
                            activeTask.interactionType === type ? "#10b981" : "#f8fafc",
                          color: activeTask.interactionType === type ? "white" : "#475569",
                          border: `2px solid ${
                            activeTask.interactionType === type ? "#10b981" : "#e2e8f0"
                          }`,
                          borderRadius: "8px",
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                        }}
                        onClick={() => handleFieldChange("interactionType", type)}
                      >
                        {type.replace("_", " ").toUpperCase()}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontWeight: "600",
                    color: "#374151",
                  }}
                >
                  Environment:
                </label>
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                  {["normal", "people in Background", "other voices"].map((env) => (
                    <button
                      type="button"
                      key={env}
                      style={{
                        padding: "8px 16px",
                        fontSize: "14px",
                        fontWeight: "500",
                        backgroundColor:
                          activeTask.environment === env ? "#f59e0b" : "#f8fafc",
                        color: activeTask.environment === env ? "white" : "#475569",
                        border: `2px solid ${
                          activeTask.environment === env ? "#f59e0b" : "#e2e8f0"
                        }`,
                        borderRadius: "8px",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        textTransform: "capitalize",
                      }}
                      onClick={() => handleFieldChange("environment", env)}
                    >
                      {env}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Comments */}
          <div className="panel" style={{ gridColumn: "1 / -1" }}>
            <h3>Comments</h3>
            <textarea
              value={activeTask.comments || ""}
              onChange={(e) => {
                const newValue = e.target.value;
                handleFieldChange("comments", newValue);
                if (commentsDebounceTimeoutRef.current)
                  clearTimeout(commentsDebounceTimeoutRef.current);
                commentsDebounceTimeoutRef.current = setTimeout(() => {
                  if (!commentsChangedRef.current && newValue.trim() !== "") {
                    logEvent("COMMENTS_ADDED", {
                      task_id: activeTaskId,
                      comments_length: newValue.length,
                    });
                    commentsChangedRef.current = true;
                  }
                }, 1000);
              }}
              placeholder="Add any additional comments..."
              rows="3"
              style={{
                width: "100%",
                padding: "12px",
                border: "2px solid #e2e8f0",
                borderRadius: "8px",
              }}
            />
          </div>
        </section>

        <footer
          className="save-bar"
          style={{
            position: "sticky",
            bottom: "0",
            backgroundColor: "var(--bg-slate)",
            padding: "12px 20px",
          }}
        >
          <div className="summary">
            <span>Task: </span>
            <strong>{activeTask.id}</strong>
          </div>
          <div className="save-actions">
            <button type="button" className="ghost" onClick={handleSkip}>
              Skip
            </button>
            <button
              type="button"
              className="primary"
              onClick={markComplete}
              disabled={submitDisabled}
              style={{
                backgroundColor: "#28a745",
                opacity: submitDisabled ? 0.5 : 1,
              }}
            >
              Submit
            </button>
          </div>
        </footer>
      </main>
    </div>
  );
}

function TranscriptCard({
  title,
  selected,
  onSelect,
  badge,
  badgeColor,
  empty,
  emptyMessage,
  copyText,
  children,
}) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async (e) => {
    e.stopPropagation();
    const ok = await copyToClipboard(copyText || "");
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };
  const showCopyBtn = copyText && copyText.trim().length > 0;
  return (
    <div
      onClick={onSelect}
      style={{
        border: `2px solid ${selected ? "#3b82f6" : "#e5e7eb"}`,
        background: selected ? "#eff6ff" : "#ffffff",
        borderRadius: "8px",
        cursor: "pointer",
        overflow: "hidden",
        transition: "all 0.15s ease",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          padding: "10px 12px",
          borderBottom: "1px solid #e5e7eb",
          backgroundColor: selected ? "#dbeafe" : "#f9fafb",
        }}
      >
        <input
          type="radio"
          checked={!!selected}
          onChange={onSelect}
          style={{ width: "16px", height: "16px", accentColor: "#3b82f6", cursor: "pointer" }}
        />
        <span style={{ fontWeight: 600, fontSize: 14, color: "#374151" }}>{title}</span>
        {badge && (
          <span
            style={{
              fontSize: 11,
              padding: "2px 8px",
              borderRadius: 999,
              background: badgeColor || "#7c3aed",
              color: "#fff",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
            }}
          >
            {badge}
          </span>
        )}
        {showCopyBtn && (
          <button
            type="button"
            onClick={handleCopy}
            title="Copy transcript"
            style={{
              marginLeft: "auto",
              padding: "4px 10px",
              fontSize: 12,
              fontWeight: 500,
              border: `1px solid ${copied ? "#10b981" : "#d1d5db"}`,
              background: copied ? "#ecfdf5" : "#ffffff",
              color: copied ? "#047857" : "#374151",
              borderRadius: 6,
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            {copied ? "✓ Copied" : "Copy"}
          </button>
        )}
      </div>
      {empty ? (
        <div style={{ padding: "16px 12px", color: "#9ca3af", fontStyle: "italic", fontSize: 14 }}>
          {emptyMessage}
        </div>
      ) : (
        children
      )}
    </div>
  );
}
